/** @format */

import React from 'react';
import SuperAgent from 'superagent';
import update from 'immutability-helper';
import {
    COLOR_4C,
    LAMINATE_GLOSS_FINISH,
    PRODUCT_TYPE_LABEL,
    PROOF_TYPE_ELECTRONIC,
    SUBSTRATE_WHITE_PLASTIC
} from 'frontier-constants';
import {parseEntityError} from 'frontier-form';
import dot from 'dot-object';

export default ComponentContainer => {
    return class extends React.Component {
        constructor(props) {
            super(props);

            const {defaultOrder} = props;
            this.state = {
                order: defaultOrder,
                isUpdating: false,
                isSaving: false,
                quotedTotal: 0
            };
        }

        handleChange = updateTarget => {
            this.setState(
                state => update(state, updateTarget),
                () => {
                    this.handleGetQuote();
                }
            );
        };

        handleGetQuote = () => {
            if (typeof this.abortController === 'undefined') {
                this.abortController = new AbortController();
            } else {
                this.abortController.abort('New Request, Cancel Outdated Request');
                this.abortController = new AbortController();
            }

            if (typeof this.updateTimeout !== 'undefined') {
                clearTimeout(this.updateTimeout);
            }

            this.updateTimeout = setTimeout(() => {
                this.setState({isUpdating: true}, async () => {
                    try {
                        let endpoint = `/orders/quoter`;
                        if (typeof this.state.order.id !== 'undefined') {
                            endpoint += `/${this.state.order.id}`;
                        }
                        const response = await fetch(endpoint, {
                            signal: this.abortController.signal,
                            method: 'POST',
                            body: JSON.stringify(this.mapsStateToRequest()),
                            headers: {
                                Accept: 'application/json',
                                'Content-Type': 'application/json'
                            }
                        });

                        const responseBody = await response.json();
                        this.mapQuoteToState(responseBody);
                    } catch (e) {
                        this.setState({isUpdating: false});
                    }
                });
            }, 1000);
        };

        mapQuoteToState(responseBody) {
            const {order} = responseBody;
            const updatedOrder = this.mapOrderToState(order);

            const updateTarget = {
                isUpdating: {$set: false},
                order: {$set: updatedOrder},
                quotedTotal: {$set: order.quoted_total},
                uploads: {$set: order.order_uploads}
            };

            this.setState(state => update(state, updateTarget));
        }

        mapOrderToState = order => {
            const update = {
                id: order.id,
                user_id: order.user_id,
                size_id: order.size_id,
                status_id: order.status_id,
                product_type_id: order.product_type_id,
                size_s3key: order.size_s3key,
                size_type_id: order.size_type_id,
                shape_id: order.shape_id,
                width: order.width,
                length: order.length,
                depth: order.depth,
                is_reorder: Boolean(order.is_reorder),
                is_square_corners: Boolean(order.is_square_corners),
                form_factor_id: order.form_factor_id,
                roll_direction_id: order.roll_direction_id,
                size_description: order.size_description, // for gtag tracking
                color_id: order.color_id,
                color_label: order.color_label, // for gtag tracking
                substrate_id: order.substrate_id,
                is_customizable_backing: order.is_customizable_backing,
                substrate_label: order.substrate_label, // for gtag tracking
                laminate_id: order.laminate_id,
                laminate_label: order.laminate_label, // for gtag tracking
                is_variable_data: Boolean(order.is_variable_data),
                is_variable_print_only: Boolean(order.is_variable_print_only),
                is_printed_proof_only: Boolean(order.is_printed_proof_only),
                variable_data_special_requests: order.variable_data_special_requests,
                processing_id: order.processing_id,
                proof_type_id: order.proof_type_id,
                design_qty: order.design_qty,
                total_qty: order.total_qty,
                design_count: order.design_count, // Counter Cached field, do not update
                is_color_matching: order.is_color_matching,
                pantone_qty: order.pantone_qty,
                pantone_comments: order.pantone_comments,
                delay_total: order.delay_total,
                delay_processing: order.delay_processing,
                comments_customer: order.comments_customer,
                comments_preprinting: order.comments_preprinting,
                quoted_total: order.quoted_total, // The CostPlusQuoter->subtotal(), no shipping
                customer_label: order.customer_label
            };

            return update;
        };

        mapsStateToRequest = () => {
            const {order} = this.state;
            return {
                order: {
                    size_id: order.size_id,
                    size_type_id: order.size_type_id,
                    product_type_id: PRODUCT_TYPE_LABEL,
                    shape_id: order.shape_id,
                    width: order.width,
                    length: order.length,
                    depth: order.depth,
                    total_qty: order.total_qty,
                    design_qty: order.design_qty,
                    is_square_corners: Boolean(order.is_square_corners),
                    form_factor_id: order.form_factor_id,
                    roll_direction_id: order.roll_direction_id,
                    color_id: order.color_id,
                    substrate_id: order.substrate_id,
                    laminate_id: order.laminate_id,
                    is_variable_data: Boolean(order.is_variable_data),
                    is_variable_print_only: Boolean(order.is_variable_print_only),
                    is_printed_proof_only: Boolean(order.is_printed_proof_only),
                    variable_data_special_requests: order.variable_data_special_requests,
                    processing_id: order.processing_id,
                    proof_type_id: order.proof_type_id,
                    is_color_matching: order.is_color_matching,
                    comments_customer: order.comments_customer,
                    comments_preprinting: order.comments_preprinting,
                    quoter_version_id: 'advanced',
                    customer_label: order.customer_label
                }
            };
        };

        handleDelete = () => {
            this.props.onDeleteQuote(this.props.quoteId);
        };

        handleAddQuote = () => {
            this.props.onAddQuote(this.state.order);
        };

        handleSave = () => {
            this.setState({isSaving: true}, async () => {
                try {
                    const {
                        body: {cart}
                    } = await SuperAgent.post(`/carts/save-to-account/${this.state.order.id}`)
                        .set('accept', 'application/json')
                        .send({customer_label: this.state.order.customer_label});

                    this.props.onDeleteQuote(this.props.quoteId);
                } catch (e) {
                    const error = parseEntityError(dot.pick('response.body', e));
                    this.setState({error, isSaving: false});
                }
            });
        };

        render() {
            const props = {...this.props};

            props.onChange = this.handleChange;
            props.onDelete = this.handleDelete;
            props.onAddQuote = this.handleAddQuote;
            props.onSave = this.handleSave;

            return <ComponentContainer {...props} {...this.state} />;
        }
    };
};
